import type { Contact } from "../api/types";
import { absent } from "../lib/format";

/* The words and marks that mean the same thing on every screen. */

/** A word in its tone, for a table cell. The Badge is for a header, where a
 *  pill reads as a label. In a table a plain colored word keeps the row
 *  quiet. */
export function Word({ tone, title, children }: {
  tone: "ok" | "waiting" | "refusal" | "quiet"; title?: string; children: string;
}) {
  return <span className={`word word-${tone}`} title={title ?? ""}>{children}</span>;
}

/** An employment or relationship standing. Unknown renders as not confirmed,
 *  never as current. */
export function StandingWord({ standing, unconfirmed }: { standing: string | null | undefined; unconfirmed?: boolean }) {
  if (standing === "current") {
    return (
      <>
        <Word tone="ok">Current</Word>
        {unconfirmed ? <span className="muted" title="Current, not confirmed recently"> (unconfirmed)</span> : null}
      </>
    );
  }
  if (standing === "ended") return <Word tone="quiet">Ended</Word>;
  return <Word tone="quiet" title="Not confirmed">Not confirmed</Word>;
}

/* The functions the lake classifies a title into (ingestion/titles.py), with
   the color each wears as a dot beside its word. The hues are the first six
   slots of the validated categorical palette in the data visualization rules,
   assigned in this fixed order and never cycled, and the word is always
   printed, so the dot adds to it and never stands alone. */
export const FUNCTION_WORDS: Record<string, string> = {
  development: "Development",
  construction: "Construction",
  asset_management: "Asset management",
  property_management: "Property management",
  operations: "Operations",
  sales: "Sales",
};

export function FunctionWord({ fn }: { fn: string | null | undefined }) {
  if (!fn || !FUNCTION_WORDS[fn]) return <>{absent()}</>;
  return <span className="fn"><span className={`fn-dot fn-${fn}`} aria-hidden="true" />{FUNCTION_WORDS[fn]}</span>;
}

/* The standing tokens, for a menu that offers them. The cell is StandingWord. */
export const STANDING_WORDS: Record<string, string> = {
  current: "Current",
  ended: "Ended",
  unknown: "Not confirmed",
};

/* What the engine concluded about a standing it has not confirmed lately
   (lake.employment.effective_standing): current, presumed current, or not
   confirmed. The token presumed_current reached the person page raw until
   the 2026-10-08 audit. */
export const EFFECTIVE_STANDING_WORDS: Record<string, string> = {
  current: "Confirmed current",
  presumed_current: "Presumed current",
  unknown: "Not confirmed",
};

export const effectiveStandingWord = (v: string | null | undefined) =>
  v ? (EFFECTIVE_STANDING_WORDS[v] ?? v.replace(/_/g, " ")) : null;

/* The score factors a campaign run records in each member's breakdown
   (api.score_factor_names), in words close enough to the tokens that
   nothing is claimed about a factor the engine does not say. One home for
   these in the engine, an api.word view, is on the engine list. */
export const FACTOR_WORDS: Record<string, string> = {
  affordable: "Affordable housing",
  any_contacts: "People connected",
  buying_contacts: "People to call",
  emails: "Emails on file",
  existing_chargers: "Existing chargers",
  geocoded: "Geocoded",
  linkedins: "LinkedIn profiles",
  mobiles: "Mobile numbers",
  not_preempted: "Not preempted",
  ordinance: "Ordinance",
  ordinance_evse: "EV ordinance",
  ordinance_local: "Local ordinance",
  owner_in_hubspot: "Owner in HubSpot",
  portfolio_depth: "Portfolio depth",
  portfolio_spread: "Portfolio spread",
  program: "Incentive program",
  program_coverage: "Program coverage",
  project_contacts: "Project contacts",
  recency: "Recency",
  right_to_charge: "Right to charge",
  senior_contacts: "Senior contacts",
  stage_heat: "Stage heat",
  units: "Units",
};

export const factorWord = (token: string) => FACTOR_WORDS[token] ?? token.replace(/_/g, " ");

/* How HubSpot names its last sales activity (hs_last_sales_activity_type). */
export const HUBSPOT_ACTIVITY_WORDS: Record<string, string> = {
  EMAIL_OPEN: "an email opened",
  EMAIL_CLICK: "an email link clicked",
  EMAIL_REPLY: "an email replied to",
  EMAIL: "an email",
  CALL: "a call",
  MEETING: "a meeting",
  NOTE: "a note",
  TASK: "a task",
  FORM_SUBMITTED: "a form filled in",
  MEETING_BOOKED: "a meeting booked",
};

export const hubspotActivityWord = (v: string | null | undefined) =>
  v ? (HUBSPOT_ACTIVITY_WORDS[v] ?? v.toLowerCase().replace(/_/g, " ")) : null;

export const LEVEL_WORDS: Record<string, string> = {
  c_suite: "C-suite",
  vp: "VP",
  director: "Director",
  regional_manager: "Regional manager",
  manager: "Manager",
  contributor: "Contributor",
  unknown: "Level unknown",
};

export const levelWord = (level: string | null | undefined) =>
  level ? (LEVEL_WORDS[level] ?? level.replace(/_/g, " ")) : null;

/* How a person can be reached, as words. A mobile first, because that is
   what a rep wants, then an email, a profile, and last an office line, which
   docs/rep-facing-copy.md says is near worthless to a rep. */
export function reachWords(p: { mobile?: string | null; email?: string | null; linkedin_url?: string | null; phone?: string | null }): string {
  return [p.mobile && "mobile", p.email && "email", p.linkedin_url && "LinkedIn", p.phone && "office line"]
    .filter(Boolean).join(", ");
}

/** The same, from the booleans the list views return. */
export function reachWordsFrom(p: { has_mobile?: boolean | null; has_email?: boolean | null; has_linkedin?: boolean | null; has_phone?: boolean | null }): string {
  return [p.has_mobile && "mobile", p.has_email && "email", p.has_linkedin && "LinkedIn", p.has_phone && "office line"]
    .filter(Boolean).join(", ");
}

/* Reach as a number to sort on: a mobile outranks an email outranks a
   profile, and an office line alone does not count as reach. */
export function reachScore(p: Contact): number {
  return (p.mobile ? 4 : 0) + (p.email ? 2 : 0) + (p.linkedin_url ? 1 : 0);
}

/** The cell: the words, muted when an office line is all there is. */
export function ReachCell({ p }: { p: Contact }) {
  const words = reachWords(p);
  if (!words) return <>{absent()}</>;
  return <span className={reachScore(p) > 0 ? "" : "muted"}>{words}</span>;
}
